import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { IdType, IIntermediaryUpdateResponse } from './intermediary.model';
import { Gender } from './clients.models';
import { BranchRequest, IAddressForm, IBranch } from './branches.model';
import {
  IInsurerLimitsOfLiabilityRequest,
  LimitOfLiabilityType,
} from './risk-request.model';
import { IQueryParams } from './queryparams';
import { IBaseModel, InsuranceCompanyNames } from './base.model';
import {
  IRepresentative,
  IRepresentativeRequest,
  UserStatus,
} from './representative.model';
import { ITenantConfig, ITenantDomain } from './tenant-config.model';

export interface IUserQueryParams extends IQueryParams {
  query?: string | null;
  partnerId?: number | null;
  insurerId?: number | null;
  brokerId?: number | null;
  agentId?: number | null;
  intermediaryId?: number | null;
  statuses: UserStatus[];
}

export interface Address {
  country: string;
  province: string;
  district: string;
  street: string;
  plotNo: string;
}

export interface InsurerRepresentative extends IRepresentative {
  insurerId: number;
  createdById: number;
  insurer: IInsurer | null;
  broker?: IIntermediaryUpdateResponse;
  gender: Gender;
}

export interface IInsurer extends IBaseModel {
  name: InsuranceCompanyNames;
  shortName: string;
  logoUrl: string;
  address: Address;
  taxPayerId: string;
  representative: IRepresentative[];
  status: string;
  email: string;
  isChecked: boolean;
  insurerType: InsurerType;
  createdById: number;
  standardLimitsOfLiabilities: IStandLimitsOfLiability[];
  partnerPortalUrl?: string;
  clientsPortalUrl?: string;
  settings?: InsurerSettings;
  branding?: ITenantConfig;
  domains?: ITenantDomain[];
}

export interface IInsurerRepresentative extends IRepresentative {
  InsurerId?: number;
}

export interface IInsurerUpdate {
  name: string;
  insurerType: InsurerType | string;
  taxPayerId: string;
  email?: string;
  shortName?: string;
  settings?: Partial<InsurerSettings>;
  address: Partial<Address>;
}

export interface IInsurerForm {
  name: FormControl<string>;
  insurerType: FormControl<InsurerType | string>;
  taxPayerId: FormControl<string>;
  email: FormControl<string>;
  shortName: FormControl<string>;
  address: FormGroup<IAddressForm>;
}

export interface IInsurerAddForm {
  name: FormControl<string>;
  insurerType: FormControl<InsurerType>;
  taxPayerId: FormControl<string>;
  email: FormControl<string>;
  shortName: FormControl<string>;
  rtsaUsername: FormControl<string>;
  rtsaPassword: FormControl<string>;
  representative: FormGroup<IInsurerRepresentativeForm>;
  branches: FormArray;
  address: FormGroup<IAddressForm>;
}

export interface IInsurerRepresentativeForm {
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  phoneNumber: FormControl<string>;
  branch: FormControl<string>;
  type: FormControl<IdType>;
  idNumber: FormControl<string>;
  physicalAddress: FormControl<string>;
  branchId: FormControl<number>;
  gender: FormControl<Gender>;
}

export interface IInsurerAddRepresentativeForm {
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  phoneNumber: FormControl<string>;
  branch: FormControl<string>;
  type: FormControl<IdType>;
  idNumber: FormControl<string>;
  physicalAddress: FormControl<string>;
  gender: FormControl<Gender>;
}

export interface IInsurerRequest {
  logoUrl: string;
  insurerType: InsurerType;
  name: string;
  email: string;
  shortName?: string;
  taxPayerId: string;
  address: Partial<Address>;
  branches: Partial<BranchRequest[]>;
  representative: Partial<IRepresentativeRequest>;
  standardLimitsOfLiabilities: Partial<IInsurerLimitsOfLiabilityRequest[]>;
  rtsaUsername: string;
  rtsaPassword: string;
}

export interface IStandLimitsOfLiability {
  id: number;
  premium: number;
  liabilityType: LimitOfLiabilityType;
}

export interface IProductLimitOfLiability extends IBaseModel {
  premium: number;
  liabilityType: LimitOfLiabilityType;
}

export interface IInsurerStats {
  totalClients: number;
  totalPolicies: number;
  totalProducts: number;
  totalGwp: number;
}

export interface IInsurersStat {
  total: number;
  totalActive: number;
  totalInactive: number;
  totalSuspended: number;
}

export interface IProductCommission {
  commissionRate: number;
}

export interface IMotorProductCommission {
  id: number;
  createdAt: string;
  updatedAt: string;
  name: string;
  code: string;
  insurer: IInsurer;
  branches: IBranch[];
  representatives: InsurerRepresentative[];
  commissionRate: number;
  classOfBusiness: ClassOfBusiness;
  description: string;
}

export interface ClassOfBusiness {
  id: number;
  createdAt: string;
  updatedAt: string;
  name: string;
}

export interface ProductCommissionRateForm {
  commissionRate: FormControl<number>;
  productId: FormControl<number>;
}

/** Mirrors the backend `InsurerType`; keep the members in step with it. */
export enum InsurerType {
  General = 'General',
  Life = 'Life',
  NonMotor = 'NonMotor',
  Motor = 'Motor',
}

export interface CommissionWindow {
  fromDay: number;
  toDay: number;
}

export interface CommissionConfig {
  enableCommissionWithdrawal: boolean;
  windows: CommissionWindow[];
}

export interface RequisitionApprovalConfig {
  /** Refuse to let the user who raised a requisition approve it themselves. */
  requireMakerChecker: boolean;
  /** Amount below which self-approval is still allowed. 0 means every amount needs a second person. */
  selfApprovalLimit?: number | null;
  /** Refuse to let a cancellation refund be moved off the account that paid the premium. */
  lockRefundToPayingAccount?: boolean | null;
}

export interface InsurerSettings extends IBaseModel {
  enableDirectWalletTransfer?: boolean | null;
  waiveDirectBusinessFee?: boolean | null;
  restrictActOnlyToUnregisteredVehicles?: boolean | null;
  commissionConfig?: CommissionConfig | null;
  requisitionApproval?: RequisitionApprovalConfig | null;
  insurerId: number;
  insurer: IInsurer | null;
  rtsaUsername: string;
  /** Write-only: sent when saving a new password, never returned by the API (see rtsaPasswordSet). */
  rtsaPassword?: string;
  /** Whether a secret is stored. The API never sends the secrets themselves. */
  rtsaPasswordSet?: boolean;
  airtelClientSecretSet?: boolean;
  mtnCredentialsSet?: boolean;
  smsOriginatorId: string;
  airtelMoney?: {
    host: string;
    grantType: string;
    clientId: string;
    clientSecret: string;
    version: string;
  };
  quotationValidityDuration: number;
  quotationValidityDurationType: string;
}
